import { createFileRoute } from '@tanstack/react-router';
import { fallback, zodValidator } from '@tanstack/zod-adapter';
import { UsersIcon } from 'lucide-react';
import { useEffect } from 'react';
import z from 'zod';

import { useGetTeamQuery } from '@/api/api-hooks';
import { DEFAULT_PAGE_SIZE } from '@/components/BaseTable/BaseTable';
import { EntityNotFound404 } from '@/components/EntityNotFound404';
import { SheetTabs } from '@/components/SheetTabs/SheetTabs';
import { SheetWithHeader } from '@/components/SheetWithHeader';
import { sourceRoleFilterValues } from '@/components/Sources/sourceRoleItems';
import { sourceVisibilityValues } from '@/components/Sources/sourceVisibilityItems';
import { TeamSettings } from '@/components/Teams/TeamSettings';
import { TeamSources } from '@/components/Teams/TeamSources';
import { TeamUsers } from '@/components/Teams/TeamUsers';
import { teamRoleFilterValues } from '@/components/Teams/teamRoleItems';
import { useUser } from '@/hooks/useUser';
import { searchParam } from '@/utils/router';
import { optionalSortingSearchParam } from '@/utils/tableSorting';

const tabTypes = ['settings', 'sources', 'users'] as const;
type TabType = (typeof tabTypes)[number];

const teamDetailsParamsSchema = z.object({
  tab: z.enum(tabTypes),
  teamId: z.string(),
  organizationName: z.string(),
});

const teamDetailsSearchSchema = z.object({
  // Shared search params
  pageSize: fallback(z.number(), DEFAULT_PAGE_SIZE).default(DEFAULT_PAGE_SIZE),
  pageIndex: fallback(z.number(), 0).default(0),
  add: fallback(z.boolean(), false).default(false),
  search: searchParam().optional(),
  // Shared between tabs; cleared on tab switch since each tab has different sortable columns.
  sorting: optionalSortingSearchParam(),
  // TeamSources
  sourceRole: z.enum(sourceRoleFilterValues).optional(),
  // TeamUsers
  teamRole: z.enum(teamRoleFilterValues).optional(),
  // Sources tab
  visibility: z.enum(sourceVisibilityValues).optional(),
});

export const Route = createFileRoute('/$organizationName/settings/_access/teams/$teamId/$tab')({
  component: TeamSheetPage,
  params: { parse: teamDetailsParamsSchema.parse },
  validateSearch: zodValidator(teamDetailsSearchSchema),
});

function TeamSheetPage() {
  const { organizationDisplayName, routeOrganizationName } = useUser();
  const { teamId, tab } = Route.useParams();
  const navigate = Route.useNavigate();

  const {
    data: team,
    isLoading: isTeamLoading,
    isError,
  } = useGetTeamQuery({ teamId }, { refetchOnWindowFocus: false, retry: false });

  useEffect(() => {
    let section = '';
    switch (tab) {
      case 'users':
        section = 'Users';
        break;
      case 'sources':
        section = 'Sources';
        break;
      case 'settings':
        section = 'Settings';
        break;
    }
    document.title = `${organizationDisplayName || 'Driver'} - ${team?.name || 'Team'} - ${section}`;
  }, [organizationDisplayName, team?.name, tab]);

  const isNotFound = isError || (!isTeamLoading && !team);
  const isTeamAdmin = team?.effective_team_role === 'team_admin';

  return (
    <SheetWithHeader
      isOpen
      renderHeaderSkeleton={isTeamLoading}
      onOpenChange={(open) => {
        if (!open) {
          navigate({
            to: '/$organizationName/settings/teams',
            params: { organizationName: routeOrganizationName },
            search: (prev) => ({
              ...prev,
              pageSize: undefined,
              pageIndex: undefined,
              add: undefined,
              search: undefined,
              sorting: undefined,
              sourceRole: undefined,
              teamRole: undefined,
              visibility: undefined,
            }),
          });
        }
      }}
      title={team ? team.name : !isTeamLoading ? 'Not found' : ''}
      description="Manage team members, sources, and settings"
      icon={<UsersIcon className="h-8 w-8" />}
      widthClassName="w-[90vw] !max-w-[760px]"
    >
      {isNotFound ? (
        <EntityNotFound404 message="Team not found" />
      ) : (
        <SheetTabs
          value={tab}
          onValueChange={(value) =>
            navigate({
              to: '/$organizationName/settings/teams/$teamId/$tab',
              params: { organizationName: routeOrganizationName, teamId, tab: value as TabType },
              // 🐉🐉🐉
              // We need to keep the search params otherwise the useEffect for the debounced search value will be triggered
              // unintentionally and redirect us back to the previous page.
              search: (prev) => ({
                ...prev,
                pageIndex: undefined,
                pageSize: undefined,
                add: undefined,
                search: undefined,
                sorting: undefined,
                role: undefined,
              }),
            })
          }
          tabs={[
            {
              value: 'users',
              label: 'Users',
              content: <TeamUsers team={team} isTeamLoading={isTeamLoading} />,
            },
            {
              value: 'sources',
              label: 'Sources',
              content: <TeamSources team={team} isTeamLoading={isTeamLoading} />,
            },
            ...(isTeamAdmin
              ? [
                  {
                    value: 'settings',
                    label: 'Settings',
                    content: <TeamSettings team={team} isTeamLoading={isTeamLoading} />,
                  },
                ]
              : []),
          ]}
        />
      )}
    </SheetWithHeader>
  );
}
